/**
 * JobDetailModal — dispatcher job detail with read + edit modes (Quick 260615-bs7 / #2).
 *
 * Opened from a job-queue row click AND a driver-stack card click. Read mode
 * shows the full job detail (GET /v1/jobs/:id). The Edit button switches to edit
 * mode where the dispatcher can change Driver, Site, Scheduled date/time, and
 * Dispatcher notes, then Save (PATCH /v1/jobs/:id) or Cancel. Tank selection is
 * still deferred (needs a job↔tank association).
 */

import * as React from 'react';
import { toast } from 'sonner';
import { useApiOpts } from '@lodex-pro/web-shared';
import { jobCreationEventLabel } from '@lodex-pro/shared/audit/job-creation-events';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { Combobox } from '../../components/Combobox';
import { useJobDetail } from './use-job-detail';
import { formatPausedTotal } from './paused-time';
import { useUpdateJob } from './use-update-job';
import { useJobTypesForCustomer } from './use-job-types';
import { useDrivers } from './use-drivers';
import { DRIVER_LOCK_COPY, driverLock, reassignErrorMessage } from './driver-lock';
import { StatusBadge } from './status-badge';
import { formatTimestamp } from '../../lib/timestamp';
import { ParkedBadge } from '../sync-conflicts/ParkedBadge';
import { parkedAuditLabel } from '../sync-conflicts/parked-labels';
import { useJobAudit, type AuditEvent, type JobAudit } from './use-completed-jobs';
import {
  BILLING_TICKET_EDITED_EVENT_TYPE,
  billingTicketEditedText,
  driverChangeDetail,
  driverChangeEventText,
  driverChangeLabel,
  siteChangeEventText,
} from './audit-labels';
import { GpsTrailModal } from './GpsTrailModal';
import { CancelJobModal } from './CancelJobModal';
import { DriverCorrectionModal } from './DriverCorrectionModal';
import { TakeBackModal } from './TakeBackModal';
import { JobSyncNudge } from '../stuck-jobs/JobSyncNudge';
import { ForceReviewModal } from '../stuck-jobs/ForceReviewModal';
import { forceReviewMode, useCompletionPolicy } from '../stuck-jobs/completion-policy';
import { VoidLoadModal } from '../stuck-jobs/VoidLoadModal';
import { ChangeSiteModal } from './ChangeSiteModal';
import { UnlinkedTanksPanel } from './UnlinkedTanksPanel';
import { siteChangeOpen } from './site-change-api';
import { canStuckCancelRole } from '../stuck-jobs/stuck-jobs-format';
import { OperatorRequestNote } from './OperatorRequestNote';
import { PlaceNoteReadOnly } from '../place-notes/PlaceNotesEditor';
import { useUserProfile } from '../../lib/use-user-timezone';
// ClickUp 86bamr1cz (DEC-122 / DEC-123) — client UX gate for the Cancel Job button
// (server is authoritative). A job is cancellable in PENDING or EN_ROUTE_TO_PICKUP
// by DISPATCH_MANAGER+ (DEC-123 collapsed the EN_ROUTE tier from TENANT_ADMIN-only
// to DISPATCH_MANAGER+), including the DEC-313 combined DISPATCH_MANAGER roles.
// CANCEL_BUTTON_ROLES additionally SHOWS the button, disabled with an explanatory
// tooltip, to DISPATCHER and the DISPATCHER_BILLING_* combined roles, and ENABLES it for them
// on a "Not started" PENDING job (OPEN-117; the server re-judges it on the locked row).
import {
  CANCEL_BUTTON_ROLES,
  CHANGE_SITE_ROLES,
  MANAGER_CANCEL_ROLES,
  REVIEW_ROLES,
  TAKE_BACK_ROLES,
} from '../../lib/role-gates';
import { DisposalDestinationsPanel } from './DisposalDestinationsPanel';
import {
  ARRIVAL_UNDONE_EVENT,
  ARRIVAL_UNDONE_LABEL,
  eventWhoLabel,
  isUndoneArrival,
} from './arrival-trigger';
import { formatNumber } from '@lodex-pro/shared/number-format';

const CANCELLABLE_STATUSES: ReadonlySet<string> = new Set(['PENDING', 'EN_ROUTE_TO_PICKUP']);

// DEC-355 — where "Void load and cancel" is offered: the in-progress statuses (the API's
// STUCK_STATUS_CODES), IN_REVIEW, and CANCELLED with a load still unvoided.
const VOIDABLE_STATUSES: ReadonlySet<string> = new Set([
  'ARRIVED_PICKUP',
  'ONSITE_WORK_START',
  'ONSITE_WORK_END',
  'EN_ROUTE_TO_DROP_OFF',
  'DISPOSAL_IN',
  'UNLOADING_START',
  'DISPOSAL_OUT',
  'PAUSED',
  'IN_REVIEW',
  'CANCELLED',
]);
const CANCEL_DISABLED_TOOLTIP = 'Requires Dispatch Manager role to cancel jobs';

export interface JobDetailModalProps {
  readonly jobId: string | null;
  readonly onClose: () => void;
}

interface UpdateBody {
  assignedDriverId: string | null;
  scheduledAt: string | null;
  dispatcherNotes: string | null;
  // DEC-146 (Phase 17) — toggle urgency on an active job.
  isUrgent: boolean;
  // Legacy "New Request" parity — editable requester contact fields (null clears).
  requesterName: string | null;
  requesterPhone: string | null;
  requesterEmail: string | null;
  // Deferred item 09.1-09 — Job Type re-selection; present only when it changed.
  jobTypeId?: number;
}

const labelStyle: React.CSSProperties = {
  fontSize: '11px',
  fontWeight: 600,
  color: 'var(--color-text-tertiary)',
};

function Field({
  label,
  value,
  prominent = false,
}: {
  label: string;
  value: React.ReactNode;
  /** Render the value large + emphasized (e.g. Net BBL on a completed ticket). */
  prominent?: boolean;
}): React.JSX.Element {
  // minWidth:0 lets the field shrink inside the grid track; wordBreak ensures long
  // values (operator names, API numbers, timestamps) wrap instead of being clipped.
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
      <span style={labelStyle}>{label}</span>
      <span
        style={
          prominent
            ? {
                fontSize: '24px',
                fontWeight: 700,
                lineHeight: 1.2,
                color: 'var(--color-text-primary)',
                wordBreak: 'break-word',
              }
            : {
                fontSize: '14px',
                lineHeight: 1.4,
                color: 'var(--color-text-primary)',
                wordBreak: 'break-word',
              }
        }
      >
        {value ?? '—'}
      </span>
    </div>
  );
}

/** Format net BBL for the detail panel: "147.3 BBL", or "—" when no capture yet. */
function formatNetBbl(netBbl: number | null | undefined): string {
  if (netBbl == null) return '—';
  return `${netBbl.toLocaleString(undefined, { maximumFractionDigits: 2 })} BBL`;
}

/** ISO 8601 → value for <input type="datetime-local"> in local time. */
function isoToLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear().toString()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  minHeight: '40px',
  padding: '8px 10px',
  fontSize: '14px',
  backgroundColor: 'var(--color-bg-tertiary)',
  border: '1px solid var(--color-border-secondary)',
  borderRadius: '10px',
  color: 'var(--color-text-primary)',
};

export function JobDetailModal({ jobId, onClose }: JobDetailModalProps): React.JSX.Element {
  const apiOpts = useApiOpts();
  const { data, isLoading, isError } = useJobDetail(apiOpts, jobId);
  const job = data?.job;

  const { data: driversData } = useDrivers(apiOpts);
  const { data: auditData } = useJobAudit(apiOpts, jobId);
  const drivers = driversData?.drivers ?? [];
  const [timelineView, setTimelineView] = React.useState<'basic' | 'detailed'>('basic');
  const [showTrail, setShowTrail] = React.useState(false);
  const [showCancel, setShowCancel] = React.useState(false);

  // Cancel Job affordance (DEC-122 / DEC-123). Role comes from the authoritative
  // server profile (GET /v1/users/me/profile) — NOT the Cognito token (which
  // carries no roleCode claim, so the old useRoleCode() returned null for every
  // user and the button never showed). Shown for PENDING or EN_ROUTE_TO_PICKUP;
  // hidden at ARRIVED_PICKUP+. DISPATCH_MANAGER+ (MANAGER_CANCEL_ROLES) get an enabled
  // button; the other CANCEL_BUTTON_ROLES (DISPATCHER, DISPATCHER_BILLING_*) get a
  // disabled button + tooltip; every other role sees no button.
  const { data: profileData } = useUserProfile();
  const role = profileData?.profile.role ?? '';
  const cancellableStatus = job !== undefined && CANCELLABLE_STATUSES.has(job.statusCode);
  const isCancelManager = MANAGER_CANCEL_ROLES.has(role);
  const showCancelButton = cancellableStatus && CANCEL_BUTTON_ROLES.has(role);
  const notStartedCancel = job?.statusCode === 'PENDING' && job.notStarted === true;
  const cancelDisabled = !isCancelManager && !notStartedCancel;
  const cancelIsEnRoute = job?.statusCode === 'EN_ROUTE_TO_PICKUP';

  // DEC-355 (OPEN-151) — "Send to review" for an in-progress job with a captured load, at any
  // time (no 24h stuck wait). The company's DEC-329 policy words the button exactly as on the
  // Stuck jobs page; the modal warns when the driver was active recently.
  const { data: policyData } = useCompletionPolicy(apiOpts);
  const forceMode = forceReviewMode(policyData?.policy ?? null, role);
  const [showForceReview, setShowForceReview] = React.useState(false);
  const sendToReview = job?.sendToReview ?? null;
  const showForceReviewButton =
    forceMode !== 'NONE' && sendToReview !== null && sendToReview.hasCapturedLoad;

  // DEC-355 (OPEN-151 PR 2) — "Void load and cancel": manager tier only (the completion policy
  // does not apply), for a job with an unvoided load that is in progress, in review, or already
  // cancelled. The API refuses an approved ticket and says why.
  const [showVoidLoad, setShowVoidLoad] = React.useState(false);
  const voidedAt = job?.captureVoidedAt ?? null;
  // OPEN-157 — a started job keeps its driver, and so does a finished or closed one.
  const lock = job === undefined ? null : driverLock(job);
  // OPEN-157 — a finished ticket's driver is corrected here, by the people who review tickets
  // (REVIEW_TICKET). The API refuses one on an invoice and says which.
  const [showDriverCorrection, setShowDriverCorrection] = React.useState(false);
  const canCorrectDriver = lock === 'finished' && REVIEW_ROLES.has(role);
  // OPEN-157 B — the one way to move a started job: Take back, DISPATCH_MANAGER and up.
  const [showTakeBack, setShowTakeBack] = React.useState(false);
  const canTakeBack = lock === 'started' && TAKE_BACK_ROLES.has(role);

  // #60 (DEC-365) — "Change site" (and the tank re-pick it may leave) up to approval, for the
  // roles holding CHANGE_JOB_SITE. The API is authoritative and says why it refuses.
  const [showChangeSite, setShowChangeSite] = React.useState(false);
  const canChangeSite =
    job !== undefined && CHANGE_SITE_ROLES.has(role) && siteChangeOpen(job.statusCode, voidedAt);
  const siteLabel =
    job === undefined
      ? null
      : [job.siteName, job.sourceWellName ?? null].filter((p) => p != null).join(' · ') || null;
  const showVoidLoadButton =
    job !== undefined &&
    canStuckCancelRole(role) &&
    job.hasCapture === true &&
    voidedAt === null &&
    VOIDABLE_STATUSES.has(job.statusCode);

  // Deferred item 09.1-09 — the Job Type picker is scoped to the job's customer
  // (hauler_operator_id), mirroring the New Job flow's useJobTypesForCustomer.
  // A NULL customer yields the unscoped (all-customers) list. The server's
  // all-customers sentinel means this is a filtered superset, not an opt-in list.
  const { data: jobTypesData } = useJobTypesForCustomer(apiOpts, job?.haulerOperatorId ?? null);
  const jobTypes = jobTypesData?.jobTypes ?? [];

  const [editing, setEditing] = React.useState(false);
  const [driverId, setDriverId] = React.useState('');
  const [scheduledLocal, setScheduledLocal] = React.useState('');
  const [notes, setNotes] = React.useState('');
  const [urgent, setUrgent] = React.useState(false);
  // Deferred item 09.1-09 — selected Job Type id (empty string until a job loads).
  const [jobTypeId, setJobTypeId] = React.useState('');
  // Legacy "New Request" parity — editable requester contact fields.
  const [requesterName, setRequesterName] = React.useState('');
  const [requesterPhone, setRequesterPhone] = React.useState('');
  const [requesterEmail, setRequesterEmail] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  // Reset edit mode whenever a different job loads / the modal reopens.
  React.useEffect(() => {
    setEditing(false);
    setError(null);
  }, [job?.id]);

  const beginEdit = React.useCallback((): void => {
    if (!job) return;
    setDriverId(job.driverId ?? '');
    setScheduledLocal(isoToLocalInput(job.scheduledAt));
    setNotes(job.dispatcherNotes ?? '');
    setUrgent(job.isUrgent ?? false);
    setJobTypeId(job.jobTypeId != null ? String(job.jobTypeId) : '');
    setRequesterName(job.requesterName ?? '');
    setRequesterPhone(job.requesterPhone ?? '');
    setRequesterEmail(job.requesterEmail ?? '');
    setError(null);
    setEditing(true);
  }, [job]);

  // Quick 260721 — the shared PATCH /v1/jobs/:id mutation (also used by the Job
  // Board inline notes cell). The hook invalidates the detail + jobs caches; this
  // callback layers the modal's edit-mode UX on top.
  const mutation = useUpdateJob(jobId, {
    onSuccess: () => {
      toast.success('Job updated');
      setEditing(false);
    },
    onError: (err: unknown) => {
      setError(
        reassignErrorMessage(err, 'Could not save changes. Check your inputs and try again.'),
      );
    },
  });

  const handleSave = React.useCallback((): void => {
    setError(null);
    // NOTE: Site is intentionally NOT in the body. Pickup site (tank battery /
    // standalone well) is set at job creation and re-pointing it re-derives the
    // hauler operator + HaulId — unsupported since Phase 26.2, so the edit-mode
    // Site field is read-only (displays the server-resolved current site). Never
    // send siteId: the server strips it, and sending it invites the "overwrite a
    // correct assignment" hazard the read-only field exists to prevent.
    const body: UpdateBody = {
      // OPEN-157 — a locked job sends its current driver (no change), so its other fields save.
      assignedDriverId: lock !== null ? (job?.driverId ?? null) : driverId || null,
      scheduledAt: scheduledLocal ? new Date(scheduledLocal).toISOString() : null,
      dispatcherNotes: notes.trim() === '' ? null : notes,
      isUrgent: urgent,
      // Requester contact — empty field clears the value (server accepts null,
      // not an empty string). Preserved as typed (not an identifier).
      requesterName: requesterName.trim() === '' ? null : requesterName.trim(),
      requesterPhone: requesterPhone.trim() === '' ? null : requesterPhone.trim(),
      requesterEmail: requesterEmail.trim() === '' ? null : requesterEmail.trim(),
    };
    // Deferred item 09.1-09 — include jobTypeId ONLY when it actually changed
    // (and never when a capture exists — the field is read-only then, so
    // jobTypeId still equals the job's current id). The server re-validates.
    const selectedJobTypeId = jobTypeId === '' ? null : Number(jobTypeId);
    if (
      selectedJobTypeId !== null &&
      job?.hasCapture !== true &&
      selectedJobTypeId !== (job?.jobTypeId ?? null)
    ) {
      body.jobTypeId = selectedJobTypeId;
    }
    mutation.mutate(body);
  }, [
    driverId,
    lock,
    scheduledLocal,
    notes,
    urgent,
    jobTypeId,
    job,
    requesterName,
    requesterPhone,
    requesterEmail,
    mutation,
  ]);

  return (
    <Dialog
      open={jobId !== null}
      onOpenChange={(next): void => {
        if (!next) {
          setEditing(false);
          onClose();
        }
      }}
    >
      {/* Panel scales to the viewport: fills available width (minus a 2rem gutter)
          up to a comfortable 56rem cap, and scrolls vertically within 90vh on short
          screens. Overrides the shared DialogContent default of `w-full max-w-lg`
          (tailwind-merge keeps the later max-w/w utilities) without affecting other
          modals that use the shared component. */}
      <DialogContent className="w-[calc(100vw-2rem)] max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          {/* #5 — Haul ID when present; PENDING jobs have no haul_id yet. */}
          <DialogTitle>{job?.haulId ? `Job ${job.haulId}` : 'Job detail'}</DialogTitle>
        </DialogHeader>

        {isLoading && <p style={{ fontSize: '14px' }}>Loading…</p>}
        {isError && (
          <p role="alert" style={{ fontSize: '14px', color: 'var(--color-danger)' }}>
            Could not load this job.
          </p>
        )}

        {job && !editing && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <StatusBadge statusCode={job.statusCode} full />
              {/* OPEN-144 PR 3 — a second run ticket is parked on this job. */}
              <ParkedBadge
                jobId={job.id}
                statusCode={job.statusCode}
                count={job.openParkedCount}
                role={role}
                onNavigate={onClose}
              />
              {/* DEC-328 — "Ask driver to sync" for a job that may be stuck on the phone. */}
              <JobSyncNudge
                jobId={job.id}
                statusCode={job.statusCode}
                driverId={job.driverId}
                role={role}
              />
            </div>
            {/* DEC-299 / Task 18.6 — the operator's note on an API haul request, first and
                labelled, so it is read before anything is changed. */}
            <OperatorRequestNote notes={job.operatorRequestNotes} testId="jdm-operator-note" />
            {/* Info grid — a FIXED responsive column count (1 → 2 → 4) instead of
                auto-fit. 13 fields never divide evenly into a fixed grid (13 is
                prime → any 2/3/4-col layout strands exactly one), so the two
                creation-metadata fields drop to the muted caption below, leaving 11
                fields grouped by meaning — identity, location, dispatch/timing — so
                the last row holds three related fields rather than a lone orphan.
                Short values (State, API number) now share a normal 1/4 track width
                instead of a sparse ≥220px auto-fit track. */}
            <div
              data-testid="jdm-detail-grid"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-4"
            >
              {/* Identity — what this ticket is and who runs it. */}
              {/* Deferred item 09.1-09 — Job Type as a LABELED field (was a bare
                  unlabeled string next to the status badge). */}
              <Field label="Job Type" value={job.jobTypeLabel} />
              {/* #4 — Haul ID (ticket number); — until issued (PENDING jobs). */}
              <Field label="Ticket ID" value={job.haulId ?? '—'} />
              {/* Field-test fix #2 (LOD000000008) — Net BBL hauled, prominent.
                  Reviewed override (Task 11.4) when set, else computed; "—" until
                  the job has a capture (i.e. pre-completion). */}
              <Field label="Net BBL" value={formatNetBbl(job.netBbl)} prominent />
              <Field label="Producer" value={job.operatorName} />
              <Field label="Driver" value={job.driverName} />
              {/* Location — where the pickup is. */}
              <Field label="Site" value={job.siteName} />
              {job.sourceWellName != null && (
                <Field label="Source well" value={job.sourceWellName} />
              )}
              <Field label="State" value={job.siteState} />
              <Field label="API number" value={job.apiNumber} />
              {/* SWD A2 (Corbin: "an intended disposal and an actual disposal") — the
                  intended (planned) and the actual destination side by side, any kind. */}
              <DisposalDestinationsPanel job={job} />
              {/* Dispatch + timing. */}
              <Field label="Dispatcher" value={job.dispatcherName} />
              <Field
                label="Scheduled"
                value={job.scheduledAt ? formatTimestamp(job.scheduledAt) : '—'}
              />
              <Field
                label="Completed"
                value={job.completedAt ? formatTimestamp(job.completedAt) : '—'}
              />
              {/* OPEN-153 / DEC-354 — total paused time, derived from the event chain. */}
              <Field label="Paused" value={formatPausedTotal(job.pausedSeconds, job.pauseCount)} />
            </div>
            {/* Requester contact — legacy "New Request" parity. Shown only when the
                job carries any requester detail (kept out of the operational grid to
                stay quiet on the common empty case; always editable via Edit). */}
            {(job.requesterName ?? job.requesterPhone ?? job.requesterEmail) != null && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-4">
                <Field label="Requester" value={job.requesterName ?? '—'} />
                <Field label="Requester phone" value={job.requesterPhone ?? '—'} />
                <Field label="Requester email" value={job.requesterEmail ?? '—'} />
              </div>
            )}
            {/* Creation metadata — least operationally relevant, so a single muted
                caption rather than two prominent grid cells (was the "Created by" +
                "Created" fields). Same data, de-emphasized. */}
            <p style={{ margin: 0, fontSize: '12px', color: 'var(--color-text-tertiary)' }}>
              Created by {job.createdByName ?? '—'} · {formatTimestamp(job.createdAt)}
            </p>
            {canChangeSite && (
              <UnlinkedTanksPanel
                jobId={job.id}
                unlinkedTankNames={job.siteChangeTankOutcome?.unlinkedTankNames ?? []}
              />
            )}
            {/* #45 / DEC-359 choice 9 — the notes for drivers on this job's pickup and
                destination, read-only here (edited in Sites & Operators). Each renders
                nothing when the place has no note. */}
            {job.pickupWellId != null ? (
              <PlaceNoteReadOnly
                label="Pickup notes for drivers"
                kind="WELL"
                id={job.pickupWellId}
              />
            ) : (
              <PlaceNoteReadOnly label="Pickup notes for drivers" kind="LOCATION" id={job.siteId} />
            )}
            {/* The current destination's notes: a facility's, a drop-off location's, or an
                authorized disposal well's (its catalog well's note, SWD follow-up). */}
            <PlaceNoteReadOnly
              label="Destination notes for drivers"
              kind={
                job.currentDropoff?.kind === 'HAULER_LOCATION'
                  ? 'LOCATION'
                  : job.currentDropoff?.kind === 'DISPOSAL_WELL'
                    ? 'WELL'
                    : 'FACILITY'
              }
              id={
                // No currentDropoff (an older API): the planned facility, as before. A disposal
                // well's note is its catalog well's; with no well id, none, never another place's.
                job.currentDropoff === undefined
                  ? (job.plannedDisposalFacility?.id ?? null)
                  : job.currentDropoff?.kind === 'DISPOSAL_WELL'
                    ? (job.currentDropoff.wellId ?? null)
                    : job.currentDropoff?.kind === 'HAULER_LOCATION' ||
                        job.currentDropoff?.kind === 'DISPOSAL_FACILITY'
                      ? job.currentDropoff.id
                      : null
              }
            />
            {/* Notes side by side on wide panels (each is usually one short line),
                falling back to stacked on narrow ones. */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <span style={labelStyle}>Dispatcher notes</span>
                <span
                  style={{
                    fontSize: '14px',
                    color: job.dispatcherNotes
                      ? 'var(--color-text-primary)'
                      : 'var(--color-text-tertiary)',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {job.dispatcherNotes ?? 'No notes'}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <span style={labelStyle}>Driver notes</span>
                <span
                  style={{
                    fontSize: '14px',
                    color: job.driverNotes
                      ? 'var(--color-text-primary)'
                      : 'var(--color-text-tertiary)',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {job.driverNotes ?? 'No notes'}
                </span>
              </div>
            </div>
            {auditData && (
              <AuditTimeline audit={auditData} view={timelineView} onView={setTimelineView} />
            )}
          </div>
        )}

        {job && editing && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {/* Also while ASSIGNING: the edit form is where the driver is chosen, and the
                operator's note (gate codes, access roads) is what that choice depends on. */}
            <OperatorRequestNote notes={job.operatorRequestNotes} testId="jdm-operator-note-edit" />
            <div>
              <label
                htmlFor="jdm-driver"
                style={{ ...labelStyle, display: 'block', marginBottom: '4px' }}
              >
                Driver
              </label>
              {lock !== null ? (
                <>
                  <div id="jdm-driver" data-testid="jdm-driver-locked" style={inputStyle}>
                    {job.driverName ?? 'Unassigned'}
                  </div>
                  <p
                    data-testid="jdm-driver-locked-reason"
                    style={{ fontSize: '12px', margin: '4px 0 0' }}
                  >
                    {DRIVER_LOCK_COPY[lock]}
                  </p>
                  {canTakeBack && (
                    <Button
                      type="button"
                      variant="outline"
                      data-testid="jdm-take-back"
                      style={{ marginTop: '6px' }}
                      onClick={(): void => setShowTakeBack(true)}
                    >
                      Take back…
                    </Button>
                  )}
                  {canCorrectDriver && (
                    <Button
                      type="button"
                      variant="outline"
                      data-testid="jdm-correct-driver"
                      style={{ marginTop: '6px' }}
                      onClick={(): void => setShowDriverCorrection(true)}
                    >
                      Correct driver…
                    </Button>
                  )}
                </>
              ) : (
                <Combobox
                  id="jdm-driver"
                  value={driverId}
                  onChange={setDriverId}
                  options={drivers.map((d) => ({ value: d.driverId, label: d.driverName }))}
                  placeholder="Unassigned"
                  searchPlaceholder="Search drivers…"
                  emptyText="No drivers match."
                />
              )}
            </div>
            {/* Deferred item 09.1-09 — Job Type selector, scoped to the job's
                customer. LOCKED read-only once a run ticket has been captured
                (client mirror of the server's JOB_TYPE_LOCKED_AFTER_CAPTURE
                guard). On save, jobTypeId is sent only when it actually changed. */}
            <div>
              <label
                htmlFor="jdm-job-type"
                style={{ ...labelStyle, display: 'block', marginBottom: '4px' }}
              >
                Job Type
              </label>
              {job.hasCapture === true ? (
                <>
                  <div
                    data-testid="jdm-edit-job-type-locked"
                    style={{
                      ...inputStyle,
                      display: 'flex',
                      alignItems: 'center',
                      backgroundColor: 'var(--color-bg-secondary)',
                    }}
                  >
                    {job.jobTypeLabel}
                  </div>
                  <span
                    style={{
                      display: 'block',
                      marginTop: '4px',
                      fontSize: '12px',
                      color: 'var(--color-text-tertiary)',
                    }}
                  >
                    Can&apos;t change Job Type after a ticket has been captured for this job
                  </span>
                </>
              ) : (
                <select
                  id="jdm-job-type"
                  data-testid="jdm-edit-job-type"
                  value={jobTypeId}
                  onChange={(e): void => {
                    setJobTypeId(e.target.value);
                  }}
                  style={inputStyle}
                >
                  {/* If the job's current type is not in the scoped list (e.g. a
                      superseded version, or a customer-scoping change), still show
                      it as the selected option so the picker isn't silently blank. */}
                  {job.jobTypeId != null && !jobTypes.some((jt) => jt.id === job.jobTypeId) && (
                    <option value={String(job.jobTypeId)}>{job.jobTypeLabel}</option>
                  )}
                  {jobTypes.map((jt) => (
                    <option key={jt.id} value={String(jt.id)}>
                      {jt.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
            {/* Site is READ-ONLY in edit mode. The pickup site (tank battery /
                standalone well, new location model) is chosen at job creation;
                re-pointing it re-derives the hauler operator + HaulId and has been
                unsupported since Phase 26.2. We show the server-resolved siteName
                (COALESCE(tank_battery.name, well.well_name) from GET /v1/jobs/:id —
                populated for BOTH pickup kinds), so the current site displays
                correctly instead of the old dead-options "Unassigned". */}
            <div>
              <span style={{ ...labelStyle, display: 'block', marginBottom: '4px' }}>Site</span>
              <div
                data-testid="jdm-edit-site"
                style={{
                  ...inputStyle,
                  display: 'flex',
                  alignItems: 'center',
                  color: job.siteName ? 'var(--color-text-primary)' : 'var(--color-text-tertiary)',
                  // Read-only affordance: dimmed background, no text cursor.
                  backgroundColor: 'var(--color-bg-secondary)',
                }}
              >
                {job.siteName ?? 'Unassigned'}
              </div>
              <span
                style={{
                  display: 'block',
                  marginTop: '4px',
                  fontSize: '12px',
                  color: 'var(--color-text-tertiary)',
                }}
              >
                Set at job creation
              </span>
            </div>
            <div>
              <label
                htmlFor="jdm-scheduled"
                style={{ ...labelStyle, display: 'block', marginBottom: '4px' }}
              >
                Scheduled
              </label>
              <input
                id="jdm-scheduled"
                type="datetime-local"
                value={scheduledLocal}
                onChange={(e): void => {
                  setScheduledLocal(e.target.value);
                }}
                style={inputStyle}
              />
            </div>
            {/* Requester contact — legacy "New Request" parity. Optional; clearing a
                field removes the value. Free-text contact info, preserved as typed. */}
            <div>
              <label
                htmlFor="jdm-requester-name"
                style={{ ...labelStyle, display: 'block', marginBottom: '4px' }}
              >
                Requester name
              </label>
              <input
                id="jdm-requester-name"
                type="text"
                maxLength={200}
                value={requesterName}
                onChange={(e): void => {
                  setRequesterName(e.target.value);
                }}
                placeholder="Who requested this haul"
                style={inputStyle}
              />
            </div>
            <div>
              <label
                htmlFor="jdm-requester-phone"
                style={{ ...labelStyle, display: 'block', marginBottom: '4px' }}
              >
                Requester phone
              </label>
              <input
                id="jdm-requester-phone"
                type="tel"
                maxLength={50}
                value={requesterPhone}
                onChange={(e): void => {
                  setRequesterPhone(e.target.value);
                }}
                placeholder="Contact phone"
                style={inputStyle}
              />
            </div>
            <div>
              <label
                htmlFor="jdm-requester-email"
                style={{ ...labelStyle, display: 'block', marginBottom: '4px' }}
              >
                Requester email
              </label>
              <input
                id="jdm-requester-email"
                type="email"
                maxLength={254}
                value={requesterEmail}
                onChange={(e): void => {
                  setRequesterEmail(e.target.value);
                }}
                placeholder="Contact email"
                style={inputStyle}
              />
            </div>
            <div>
              <label
                htmlFor="jdm-notes"
                style={{ ...labelStyle, display: 'block', marginBottom: '4px' }}
              >
                Dispatcher notes
              </label>
              <textarea
                id="jdm-notes"
                value={notes}
                onChange={(e): void => {
                  setNotes(e.target.value);
                }}
                rows={4}
                placeholder="Add notes for this job…"
                style={{ ...inputStyle, resize: 'vertical' }}
              />
            </div>
            {/* Urgent toggle — DEC-146 (Phase 17). */}
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={urgent}
                onChange={(e): void => {
                  setUrgent(e.target.checked);
                }}
                aria-label="Mark job urgent"
              />
              <span
                style={{ fontSize: '13px', color: 'var(--color-text-primary)', fontWeight: 600 }}
              >
                Urgent
              </span>
            </label>
            {error && (
              <p role="alert" style={{ fontSize: '13px', color: 'var(--color-danger)', margin: 0 }}>
                {error}
              </p>
            )}
          </div>
        )}

        {voidedAt !== null && (
          <p
            data-testid="job-load-voided"
            style={{ fontSize: '13px', color: 'var(--color-text-secondary)', margin: 0 }}
          >
            {`Load voided ${formatTimestamp(voidedAt)}`}
            {job?.captureVoidedReason ? ` — ${job.captureVoidedReason}` : ''}. The operator does not
            see it.
          </p>
        )}

        <DialogFooter>
          {!editing ? (
            <>
              <Button type="button" variant="outline" onClick={onClose}>
                Close
              </Button>
              {/* Phase 8 / Task 8.5 — GPS breadcrumb trail. */}
              <Button
                type="button"
                variant="outline"
                disabled={!job}
                onClick={(): void => setShowTrail(true)}
              >
                View GPS trail
              </Button>
              {/* ClickUp 86bamr1cz (DEC-122 / DEC-123) — Cancel Job. Enabled for
                  DISPATCH_MANAGER+ (red for PENDING, amber for a driver-accepted
                  EN_ROUTE job); shown DISABLED + tooltip for plain DISPATCHER. */}
              {showCancelButton && (
                <Button
                  type="button"
                  disabled={cancelDisabled}
                  title={cancelDisabled ? CANCEL_DISABLED_TOOLTIP : undefined}
                  aria-label={cancelDisabled ? CANCEL_DISABLED_TOOLTIP : 'Cancel Job'}
                  onClick={cancelDisabled ? undefined : (): void => setShowCancel(true)}
                  style={
                    cancelDisabled
                      ? {
                          backgroundColor: 'var(--color-bg-tertiary)',
                          color: 'var(--color-text-tertiary)',
                          borderColor: 'var(--color-border-secondary)',
                          cursor: 'not-allowed',
                          opacity: 0.6,
                        }
                      : cancelIsEnRoute
                        ? {
                            backgroundColor: 'var(--color-warning)',
                            color: 'var(--color-on-status)',
                            borderColor: 'var(--color-warning)',
                          }
                        : {
                            backgroundColor: 'var(--color-danger)',
                            color: 'var(--color-on-accent)',
                            borderColor: 'var(--color-danger)',
                          }
                  }
                >
                  Cancel Job
                </Button>
              )}
              {showForceReviewButton && (
                <Button
                  type="button"
                  onClick={(): void => setShowForceReview(true)}
                  style={{
                    backgroundColor: 'var(--color-warning)',
                    color: 'var(--color-on-status)',
                    borderColor: 'var(--color-warning)',
                  }}
                >
                  {forceMode === 'REQUEST' ? 'Ask to send to review' : 'Send to review'}
                </Button>
              )}
              {canChangeSite && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={(): void => setShowChangeSite(true)}
                >
                  Change site
                </Button>
              )}
              {showVoidLoadButton && (
                <Button
                  type="button"
                  onClick={(): void => setShowVoidLoad(true)}
                  style={{
                    backgroundColor: 'var(--color-danger)',
                    color: 'var(--color-on-accent)',
                    borderColor: 'var(--color-danger)',
                  }}
                >
                  Void load and cancel
                </Button>
              )}
              <Button
                type="button"
                disabled={!job}
                onClick={beginEdit}
                style={{
                  backgroundColor: 'var(--color-accent-blue)',
                  color: 'var(--color-on-accent)',
                  borderColor: 'var(--color-accent-blue)',
                }}
              >
                Edit
              </Button>
            </>
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={(): void => {
                  setEditing(false);
                  setError(null);
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                disabled={mutation.isPending}
                onClick={handleSave}
                style={{
                  backgroundColor: 'var(--color-accent-blue)',
                  color: 'var(--color-on-accent)',
                  borderColor: 'var(--color-accent-blue)',
                }}
              >
                {mutation.isPending ? 'Saving…' : 'Save'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
      <GpsTrailModal
        apiOpts={apiOpts}
        jobId={jobId}
        haulId={job?.haulId ?? null}
        open={showTrail}
        onClose={(): void => setShowTrail(false)}
      />
      <CancelJobModal
        jobId={showCancel ? (job?.id ?? null) : null}
        haulId={job?.haulId ?? null}
        statusCode={job?.statusCode ?? null}
        open={showCancel}
        onClose={(): void => setShowCancel(false)}
        onCancelled={(): void => {
          setShowCancel(false);
          onClose();
        }}
      />
      <TakeBackModal
        jobId={showTakeBack ? (job?.id ?? null) : null}
        haulId={job?.haulId ?? null}
        driverId={job?.driverId ?? null}
        driverName={job?.driverName ?? null}
        drivers={drivers}
        open={showTakeBack}
        onClose={(): void => setShowTakeBack(false)}
        onTakenBack={(): void => {
          setShowTakeBack(false);
          onClose();
        }}
      />
      <DriverCorrectionModal
        jobId={showDriverCorrection ? (job?.id ?? null) : null}
        haulId={job?.haulId ?? null}
        driverId={job?.driverId ?? null}
        driverName={job?.driverName ?? null}
        drivers={drivers}
        open={showDriverCorrection}
        onClose={(): void => setShowDriverCorrection(false)}
        onCorrected={(): void => {
          setShowDriverCorrection(false);
          onClose();
        }}
      />
      <ChangeSiteModal
        target={
          showChangeSite && job !== undefined
            ? {
                jobId: job.id,
                haulId: job.haulId,
                currentLabel: siteLabel,
                hasCapture: job.hasCapture === true,
              }
            : null
        }
        open={showChangeSite}
        onClose={(): void => setShowChangeSite(false)}
        onChanged={(): void => setShowChangeSite(false)}
      />
      <VoidLoadModal
        target={showVoidLoad && job !== undefined ? { jobId: job.id, haulId: job.haulId } : null}
        open={showVoidLoad}
        onClose={(): void => setShowVoidLoad(false)}
        onVoided={(): void => {
          setShowVoidLoad(false);
          onClose();
        }}
      />
      <ForceReviewModal
        row={
          showForceReview && job !== undefined && sendToReview !== null
            ? {
                jobId: job.id,
                haulId: job.haulId,
                driverName: job.driverName,
                lastActivityAt: sendToReview.lastActivityAt,
                onStuckList: sendToReview.onStuckList,
              }
            : null
        }
        open={showForceReview}
        onClose={(): void => setShowForceReview(false)}
        onSent={(): void => {
          setShowForceReview(false);
          onClose();
        }}
        asRequest={forceMode === 'REQUEST'}
      />
    </Dialog>
  );
}

// ── Audit timeline (Quick 260616-cmpl) ───────────────────────────────────────

function whoOf(e: AuditEvent | undefined): string {
  if (e === undefined) return '—';
  // OPEN-220a / DEC-386 — "· Automatic" after an arrival the phone's geofence recorded.
  return eventWhoLabel(e);
}

/** A driver reprint from the completed-job menu (audit_events; Phase 22 Task 22.8 D5). */
const REPRINT_EVENT_TYPE = 'OFFICIAL_TICKET_REPRINTED';
const REPRINT_TICKET_LABELS: Readonly<Record<string, string>> = {
  PICKUP: 'Pickup ticket',
  DISPOSAL: 'Disposal ticket',
  PROVISIONAL: 'Provisional ticket',
};

function reprintTicketLabel(e: AuditEvent): string {
  if (e.ticketType == null) return 'Ticket';
  return REPRINT_TICKET_LABELS[e.ticketType] ?? e.ticketType;
}

/** An offline-created load's provisional P-id replaced by the server's HaulId (DEC-038). */
const HAUL_ID_ASSIGNED_EVENT_TYPE = 'HAUL_ID_ASSIGNED';

/**
 * DEC-316 — the creation event at the top of a job's timeline, in words.
 *
 * This timeline prints `e.eventType` VERBATIM for anything it has no case for
 * (see the title expression below), so without this a dispatcher would read
 * "JOB_CREATED_AUTO_HAUL". The labels live in
 * `@lodex-pro/shared/audit/job-creation-events` beside the list the API filters
 * on, so the event the endpoint newly admits and the words shown for it cannot
 * drift apart — and a seventh creation path has one place to add both.
 */
function creationEventLabel(e: AuditEvent): string | null {
  return jobCreationEventLabel(e.eventType);
}

/** DEC-352 — a driver record accepted with a time the reviewer should check. */
const TIME_CHECK_EVENT_TYPE = 'TIME_CHECK';
const TIME_CHECK_TEXT: Readonly<Record<string, string>> = {
  DEVICE_CLOCK: "Time check · the phone's clock was wrong",
  ARRIVED_LATE: 'Time check · a record arrived late (phone was offline)',
  CLOCK_UNKNOWN: "Time check · older app, couldn't tell clock from delay",
  // OPEN-160 — this job's pause time came from another job's flagged record.
  PAUSED_BY_FLAGGED_RECORD: "Time check · paused by another job's record whose time was off",
};

function timeCheckText(e: AuditEvent): string {
  return (e.timeCheckCase != null ? TIME_CHECK_TEXT[e.timeCheckCase] : undefined) ?? 'Time check';
}

function haulIdAssignedText(e: AuditEvent): string {
  return `Offline load ${e.provisionalHaulId ?? '—'} assigned ticket number ${e.canonicalHaulId ?? '—'}`;
}

function gpsText(e: AuditEvent): string | null {
  if (e.gpsLat == null || e.gpsLng == null) return null;
  const acc = e.gpsAccuracyFt != null ? ` (±${e.gpsAccuracyFt.toString()}ft)` : '';
  return `${formatNumber(e.gpsLat, { decimals: 5 })}, ${formatNumber(e.gpsLng, { decimals: 5 })}${acc}`;
}

/**
 * Phase 8 / Task 8.4 — GPS verification badge on audit events (§15). Green =
 * VERIFIED, amber = OUTSIDE_RANGE, grey = UNVERIFIABLE / NO_GPS. Not on the run
 * ticket; dispatcher audit timeline only.
 *
 * Field-test fix: rendered on ANY audit event that carries a verification_result
 * (previously gated to arrival events only), and surfaced on the BASIC milestone
 * view — not just the Detailed view — so a dispatcher reviewing a completed
 * ticket sees the verification result without switching tabs.
 */
const VERIFICATION_STYLE: Readonly<Record<string, { color: string; label: string }>> = {
  VERIFIED: { color: 'var(--color-success)', label: 'GPS verified' },
  OUTSIDE_RANGE: { color: 'var(--color-warning)', label: 'Outside range' },
  UNVERIFIABLE: { color: 'var(--color-text-tertiary)', label: 'Unverifiable GPS' },
  NO_GPS: { color: 'var(--color-text-tertiary)', label: 'No GPS' },
};

/** Colored pill badge for a GPS verification result (VERIFIED/OUTSIDE_RANGE/…). */
function VerificationBadge({ result }: { result: string }): React.JSX.Element {
  const m = VERIFICATION_STYLE[result] ?? {
    color: 'var(--color-text-tertiary)',
    label: result,
  };
  return (
    <span
      data-testid="gps-verification-badge"
      data-verification-result={result}
      title={m.label}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '1px 7px',
        borderRadius: '10px',
        fontSize: '11px',
        fontWeight: 600,
        color: m.color,
        border: `1px solid ${m.color}`,
        backgroundColor: 'transparent',
        whiteSpace: 'nowrap',
      }}
    >
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          backgroundColor: m.color,
          display: 'inline-block',
        }}
      />
      {m.label}
    </span>
  );
}

/** The badge for an event, or null when the event carries no verification result. */
function verificationBadgeFor(event: AuditEvent | undefined): React.JSX.Element | null {
  if (event?.verificationResult == null) return null;
  return <VerificationBadge result={event.verificationResult} />;
}

function MilestoneRow({
  label,
  ts,
  who,
  extra,
  badge,
}: {
  label: string;
  ts: string | null;
  who: string;
  extra?: string | undefined;
  /** Optional trailing badge (e.g. GPS verification on arrival milestones). */
  badge?: React.ReactNode;
}): React.JSX.Element {
  const reached = ts != null;
  // flexWrap + word-breaking let long timestamps/actor names wrap onto the next
  // line on narrow panels instead of being clipped (never truncate timeline text).
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        flexWrap: 'wrap',
        gap: '2px 8px',
        padding: '8px 0',
        borderBottom: '1px solid var(--color-border-secondary)',
        opacity: reached ? 1 : 0.5,
      }}
    >
      <span style={{ width: '130px', flexShrink: 0, fontSize: '13px', fontWeight: 600 }}>
        {label}
      </span>
      <span
        style={{
          flex: '1 1 160px',
          minWidth: 0,
          fontSize: '13px',
          color: 'var(--color-text-secondary)',
          wordBreak: 'break-word',
        }}
      >
        {reached ? formatTimestamp(ts) : '—'}
        {extra != null && extra !== '' && reached ? ` · ${extra}` : ''}
      </span>
      <span
        style={{ fontSize: '12px', color: 'var(--color-text-tertiary)', wordBreak: 'break-word' }}
      >
        {reached ? who : ''}
      </span>
      {reached && badge != null ? badge : null}
    </div>
  );
}

function AuditTimeline({
  audit,
  view,
  onView,
}: {
  audit: JobAudit;
  view: 'basic' | 'detailed';
  onView: (v: 'basic' | 'detailed') => void;
}): React.JSX.Element {
  const events = audit.events;
  const started = events.find(
    (e) =>
      e.eventType === 'JOB_ACCEPTED' || (e.source === 'timeclock' && e.eventType === 'STARTED'),
  );
  // OPEN-220b — an arrival the driver undid is not the step the job reached.
  const arrivedPickup = events.find((e) => e.eventType === 'ARRIVED_PICKUP' && !isUndoneArrival(e));
  const doneLoading = events.find(
    (e) => e.eventType === 'GAUGE_ENTRY_SAVED' || e.eventType === 'DEPARTED_PICKUP',
  );
  const arrivedDisposal = events.find(
    (e) => e.eventType === 'ARRIVED_DISPOSAL' && !isUndoneArrival(e),
  );
  const arrivalUndos = events.filter((e) => e.eventType === ARRIVAL_UNDONE_EVENT);
  const completedEv = events.find((e) => e.eventType === 'DISPOSAL_COMPLETE');
  const reprints = events.filter((e) => e.eventType === REPRINT_EVENT_TYPE);
  const haulIdAssigned = events.find((e) => e.eventType === HAUL_ID_ASSIGNED_EVENT_TYPE);
  // OPEN-157 — take-backs and driver corrections: who moved the job, from whom, to whom, why.
  const driverChanges = events.filter((e) => driverChangeLabel(e) !== null);

  const toggleBtn = (v: 'basic' | 'detailed', label: string): React.JSX.Element => (
    <button
      type="button"
      onClick={(): void => onView(v)}
      aria-pressed={view === v}
      style={{
        padding: '4px 10px',
        fontSize: '12px',
        fontWeight: 600,
        borderRadius: 'var(--radius-pill)',
        cursor: 'pointer',
        border: '1px solid var(--color-border-secondary)',
        backgroundColor: view === v ? 'var(--color-accent-blue)' : 'transparent',
        color: view === v ? 'var(--color-on-accent)' : 'var(--color-text-secondary)',
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={labelStyle}>Audit timeline</span>
        <div style={{ display: 'flex', gap: '4px' }}>
          {toggleBtn('basic', 'Basic')}
          {toggleBtn('detailed', 'Detailed')}
        </div>
      </div>

      {view === 'basic' ? (
        <div data-testid="jdm-timeline-scroll" style={{ maxHeight: '320px', overflowY: 'auto' }}>
          <MilestoneRow
            label="Job created"
            ts={audit.jobCreatedAt}
            who={audit.jobCreatedByName ?? 'System'}
          />
          <MilestoneRow
            label="Driver started"
            ts={started?.occurredAt ?? null}
            who={whoOf(started)}
          />
          <MilestoneRow
            label="Arrived pickup"
            ts={arrivedPickup?.occurredAt ?? null}
            who={whoOf(arrivedPickup)}
            badge={verificationBadgeFor(arrivedPickup)}
          />
          <MilestoneRow
            label="Done loading"
            ts={doneLoading?.occurredAt ?? null}
            who={whoOf(doneLoading)}
            extra={audit.totalBbl != null ? `${audit.totalBbl.toString()} bbl` : undefined}
          />
          <MilestoneRow
            label="Arrived disposal"
            ts={arrivedDisposal?.occurredAt ?? null}
            who={whoOf(arrivedDisposal)}
            extra={arrivedDisposal?.facilityName ?? undefined}
            badge={verificationBadgeFor(arrivedDisposal)}
          />
          <MilestoneRow
            label="Completed"
            ts={completedEv?.occurredAt ?? audit.completedAt}
            who={whoOf(completedEv)}
          />
          {haulIdAssigned != null && (
            <MilestoneRow
              label="Ticket number assigned"
              ts={haulIdAssigned.occurredAt}
              who={whoOf(haulIdAssigned)}
              extra={haulIdAssignedText(haulIdAssigned)}
            />
          )}
          {arrivalUndos.map((e, i) => (
            <MilestoneRow
              key={`arrival-undo-${e.occurredAt}-${i.toString()}`}
              label={ARRIVAL_UNDONE_LABEL}
              ts={e.occurredAt}
              who={whoOf(e)}
            />
          ))}
          {driverChanges.map((e, i) => (
            <MilestoneRow
              key={`driver-change-${e.occurredAt}-${i.toString()}`}
              label={driverChangeLabel(e) ?? e.eventType}
              ts={e.occurredAt}
              who={whoOf(e)}
              extra={driverChangeDetail(e)}
            />
          ))}
          {reprints.map((e, i) => (
            <MilestoneRow
              key={`reprint-${e.occurredAt}-${i.toString()}`}
              label="Ticket reprinted"
              ts={e.occurredAt}
              who={whoOf(e)}
              extra={
                e.online === false ? `${reprintTicketLabel(e)} · offline` : reprintTicketLabel(e)
              }
            />
          ))}
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '360px',
            overflowY: 'auto',
          }}
        >
          {events.length === 0 ? (
            <span style={{ fontSize: '13px', color: 'var(--color-text-tertiary)' }}>
              No events recorded.
            </span>
          ) : (
            events.map((e, i) => {
              const gps = gpsText(e);
              const syncedDiffers =
                e.syncedAt != null && e.syncedAt.slice(0, 16) !== e.occurredAt.slice(0, 16);
              return (
                <div
                  key={`${e.source}-${e.eventType}-${e.occurredAt}-${i.toString()}`}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '2px',
                    padding: '6px 0',
                    borderBottom: '1px solid var(--color-border-secondary)',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '4px 8px',
                    }}
                  >
                    <span style={{ fontSize: '13px', fontWeight: 600, wordBreak: 'break-word' }}>
                      {e.eventType === ARRIVAL_UNDONE_EVENT
                        ? ARRIVAL_UNDONE_LABEL
                        : isUndoneArrival(e)
                          ? `${e.eventType} · undone`
                          : e.eventType === REPRINT_EVENT_TYPE
                            ? `Ticket reprinted · ${reprintTicketLabel(e)}`
                            : e.eventType === HAUL_ID_ASSIGNED_EVENT_TYPE
                              ? haulIdAssignedText(e)
                              : e.eventType === TIME_CHECK_EVENT_TYPE
                                ? timeCheckText(e)
                                : e.eventType === BILLING_TICKET_EDITED_EVENT_TYPE
                                  ? billingTicketEditedText(e)
                                  : (parkedAuditLabel(e.eventType) ??
                                    siteChangeEventText(e) ??
                                    driverChangeEventText(e) ??
                                    creationEventLabel(e) ??
                                    e.eventType)}
                    </span>
                    {verificationBadgeFor(e)}
                    {e.online === false && (
                      <span
                        style={{
                          fontSize: '11px',
                          fontWeight: 700,
                          color: 'var(--color-warning)',
                        }}
                      >
                        OFFLINE
                      </span>
                    )}
                    {e.online === true && (
                      <span style={{ fontSize: '11px', color: 'var(--color-text-tertiary)' }}>
                        online
                      </span>
                    )}
                    <span
                      style={{
                        marginLeft: 'auto',
                        fontSize: '12px',
                        color: 'var(--color-text-tertiary)',
                      }}
                    >
                      {whoOf(e)}
                    </span>
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                    {formatTimestamp(e.occurredAt)}
                    {syncedDiffers && e.syncedAt != null
                      ? ` · synced ${formatTimestamp(e.syncedAt)}`
                      : ''}
                    {gps != null ? ` · ${gps}` : ''}
                    {e.facilityName != null && e.facilityName !== '' ? ` · ${e.facilityName}` : ''}
                  </div>
                  {e.verificationResult === 'OUTSIDE_RANGE' && (
                    <div style={{ fontSize: '12px', color: 'var(--color-warning)' }}>
                      Outside expected range — requires truck pusher review
                      {e.distanceFromCenter != null
                        ? ` (${Math.round(e.distanceFromCenter).toString()} ft from site)`
                        : ''}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
